import { SelectionMode } from '@uifabric/utilities'
import {
  EntityChangeList,
  EntityChangeListProps,
} from 'components/EntityChangeList'
import {
  ColumnRenderer,
  renderDate,
  renderEnum,
  renderStatusIcon,
} from 'components/EntityList'
import { Box } from 'grid'
import { ResourceRequirement } from 'model'
import { ConstrainMode } from 'office-ui-fabric-react'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { createFieldColumn } from 'views/helpers'

export type ResourceRequirementListProps = EntityChangeListProps<
  Partial<ResourceRequirement> & { _id: string }
>

const renderOrderIds: ColumnRenderer = (item, i, props) => {
  const content = item!.orderIdDistribution
    .map((oId: { orderId: string; percentage: number }) =>
      oId.percentage ? `${oId.orderId}, ${oId.percentage}%` : `${oId.orderId}`
    )
    .join('\n')
  return <Box style={{ whiteSpace: 'pre-line' }}>{content}</Box>
}

export const ResourceRequirementList: React.FunctionComponent<
  ResourceRequirementListProps
> = ({ items, ...props }) => {
  const { t } = useTranslation()

  const fieldColumn = React.useMemo(() => createFieldColumn(t), [t])

  const columns = React.useMemo(
    () => [
      fieldColumn('resourceRequirementType', {
        isSorted: true,

        ungroupable: true,
        onRender: (item: ResourceRequirement, i, col) => {
          if (
            item.resourceRequirementType === 'chargingInfrastructure' &&
            item.chargingInfrastructure
          ) {
            return `${renderEnum(t, 'resourceRequirementType')(item, i, col)} ${
              item.chargingInfrastructure.title
            }`
          } else if (
            item.resourceRequirementType === 'fuel' &&
            item.fuel &&
            item.fuel.title
          ) {
            return `${renderEnum(t, 'resourceRequirementType')(item, i, col)} ${
              item.fuel.title
            }`
          } else {
            return renderEnum(t, 'resourceRequirementType')(item, i, col)
          }
        },
      }),
      fieldColumn('testTrack', {
        onRender: (item: ResourceRequirement, i, col) => {
          if (item && item.testTrack) {
            return (
              <Box key={i}>
                {item.testTrack.title}{' '}
                {item.testTrackUsage ? ` (${item.testTrackUsage.title})` : ''}
              </Box>
            )
          } else {
            return <Box>{''}</Box>
          }
        },
        ungroupable: true,
        minWidth: 100,
      }),
      fieldColumn('amount', {
        onRender: (item: ResourceRequirement, i, col) => {
          if (item.racewayHours) {
            return `${item.racewayHours} h`
          } else if (item.resourceRequirementType === 'fuel') {
            return `${item.amount} L`
          } else if (
            item.resourceRequirementType === 'chargingInfrastructure'
          ) {
            return `${item.amount} kWh`
          } else {
            return item.amount ? item.amount : 1
          }
        },
        ungroupable: true,
      }),
      fieldColumn('dateFrom', { ungroupable: true, onRender: renderDate }),
      fieldColumn('dateTo', { ungroupable: true, onRender: renderDate }),
      fieldColumn('orderIdDistribution', {
        onRender: renderOrderIds,
        isMultiline: true,
        minWidth: 100,
        ungroupable: true,
      }),
      fieldColumn('status', {
        onRender: renderStatusIcon,
        ungroupable: true,
      }),
    ],
    [fieldColumn, t]
  )

  return (
    <EntityChangeList
      alternate={true}
      stickyHeader={true}
      selectionMode={SelectionMode.multiple}
      items={items}
      constrainMode={ConstrainMode.horizontalConstrained}
      columns={columns}
      {...props}
    />
  )
}
